import jsPDF from "jspdf";
import { markPdfExported } from "@/components/OnboardingChecklist";

/**
 * Export a markdown-ish text body as a clean A4 PDF.
 * Lightweight: no html canvas — pure text via jsPDF.
 */
export function downloadAsPDF({ title, body, kind }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const maxW = pageW - margin * 2;

  // Header bar
  doc.setFillColor(2, 6, 23);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(6, 182, 212);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("3C AI", margin, 24);
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("ENGINEERING INTELLIGENCE PLATFORM", margin, 38);
  doc.setTextColor(148, 163, 184);
  doc.text(new Date().toISOString().slice(0, 10), pageW - margin, 24, {
    align: "right",
  });
  doc.text((kind || "").toUpperCase(), pageW - margin, 38, { align: "right" });

  // Title
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  let y = 92;
  const titleLines = doc.splitTextToSize(title || "Document", maxW);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 22 + 6;
  doc.setDrawColor(6, 182, 212);
  doc.setLineWidth(1.2);
  doc.line(margin, y, margin + 48, y);
  y += 18;

  // Body — render markdown as light styled text
  const lineH = 14;
  doc.setTextColor(30, 41, 59);
  const lines = (body || "").split(/\r?\n/);
  for (const raw of lines) {
    if (y > pageH - margin - 24) {
      doc.addPage();
      y = margin;
    }
    let line = raw.replace(/\*\*(.+?)\*\*/g, "$1").replace(/[`*_]/g, "");
    if (line.startsWith("# ")) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      const wrapped = doc.splitTextToSize(line.slice(2), maxW);
      doc.text(wrapped, margin, y);
      y += wrapped.length * 18 + 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      continue;
    }
    if (line.startsWith("## ")) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      const wrapped = doc.splitTextToSize(line.slice(3), maxW);
      doc.text(wrapped, margin, y);
      y += wrapped.length * 16 + 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      continue;
    }
    if (line.startsWith("### ")) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      const wrapped = doc.splitTextToSize(line.slice(4), maxW);
      doc.text(wrapped, margin, y);
      y += wrapped.length * 14 + 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      continue;
    }
    if (line.startsWith("> ")) {
      doc.setTextColor(100, 116, 139);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      const wrapped = doc.splitTextToSize(line.slice(2), maxW - 12);
      doc.setDrawColor(6, 182, 212);
      doc.setLineWidth(2);
      doc.line(margin, y - 9, margin, y - 9 + wrapped.length * 12 + 4);
      doc.text(wrapped, margin + 8, y);
      y += wrapped.length * 12 + 6;
      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      continue;
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    if (!line.trim()) {
      y += lineH * 0.5;
      continue;
    }
    const wrapped = doc.splitTextToSize(line, maxW);
    doc.text(wrapped, margin, y);
    y += wrapped.length * lineH;
  }

  // Footer
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, pageH - 36, pageW - margin, pageH - 36);
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.text(
      "Generated using 3C AI — Contract Intelligence Platform",
      margin,
      pageH - 22
    );
    doc.text(`Page ${i} / ${total}`, pageW - margin, pageH - 22, {
      align: "right",
    });
  }

  const safe = (title || kind || "document")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  doc.save(`${safe}.pdf`);
  try {
    markPdfExported();
  } catch (_e) {}
}
